import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  processAdditionalContacts, 
  processPayrollSchedules, 
  ensureStringArray,
  normalizeCorporateStructure,
  processHealthPlans
} from '@/utils/application-data-utils';
import type { Application } from '@/types/database.types';
import type { Json } from '@/integrations/supabase/types';

export const useFetchApplication = () => {
  const [isLoading, setIsLoading] = useState(false);
  
  const fetchApplication = async (userId: string, applicationId?: string): Promise<Application | null> => {
    try {
      setIsLoading(true);
      
      let query = supabase
        .from('applications')
        .select('*')
        .eq('user_id', userId);
      
      // If applicationId is provided, fetch that specific application
      if (applicationId) {
        query = query.eq('id', applicationId);
      } else {
        // Otherwise, get the most recent application
        query = query.order('created_at', { ascending: false }).limit(1);
      }
      
      const { data, error } = await query.maybeSingle();
      
      if (error) {
        console.error('Error fetching application:', error);
        throw error;
      }
      
      if (!data) return null;
      
      // Process the raw data to match the Application type
      return {
        ...data,
        // Convert JSON fields to proper typed arrays
        additional_contacts: processAdditionalContacts(data.additional_contacts as Json),
        additional_schedules: processPayrollSchedules(data.additional_schedules as Json[]),
        // Process health plans properly
        health_plans: processHealthPlans(data.health_plans as Json),
        // Ensure string arrays - convert DB string values to arrays
        desired_medical_carriers: ensureStringArray(data.desired_medical_carriers),
        desired_ancillary_carriers: ensureStringArray(data.desired_ancillary_carriers),
        other_documents: ensureStringArray(data.other_documents),
        corporate_structure: normalizeCorporateStructure(data.corporate_structure),
        // Process additional array fields that might be strings in the database
        states_with_employees: ensureStringArray(data.states_with_employees),
        top_3_peo_needs: ensureStringArray(data.top_3_peo_needs),
        preferred_peos: ensureStringArray(data.preferred_peos),
        excluded_peos: ensureStringArray(data.excluded_peos),
        states_of_operation: ensureStringArray(data.states_of_operation)
      } as unknown as Application;
    } catch (error) {
      console.error('Error fetching application:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    fetchApplication,
    isLoading,
  };
};
